import { Capacitor } from '@capacitor/core';
import * as SentryCapacitor from '@sentry/capacitor';
import { Logger } from '../app/utils/logger';
import { environment } from '../environments/environment';

/**
 * @description Sentry.
 * @exports
 * @class Sentry
 * @see https://docs.sentry.io/platforms/javascript/guides/capacitor
 */
export class Sentry {
  /**
   * Creates an instance of Sentry.
   *
   * @param {Logger} logger Logger.
   * @memberof Sentry
   */
  constructor(private readonly logger: Logger) {}

  /**
   * @description Init.
   * @memberof Sentry
   */
  init(): void {
    const log = this.logger.child('Sentry');

    try {
      log.debug('init');

      SentryCapacitor.init({
        dsn: environment.SENTRY.DSN,
        enableNative: Capacitor.isNativePlatform(),
        // An array of strings or regexps that'll be used to ignore specific errors based on their type/message
        ignoreErrors: ['RangeError', 'ApolloError'],
        // To see what the Sentry SDK is doing; Helps when setting things up
        debug: environment.SENTRY.DEBUG,
        // Whether SDK should be enabled or not
        enabled: environment.SENTRY.ENABLE,
        // Use the tracing integration to see traces and add performance monitoring
        integrations: [
          // Registers and configures the Tracing integration,
          // which automatically instruments your application to monitor its
          // performance, including custom Angular routing instrumentation
          SentryCapacitor.browserTracingIntegration()
          // Registers the Replay integration,
          // SentryCapacitor automatically captures Session Replays
          // SentryCapacitor.replayIntegration() as any
        ],
        _experiments: {
          enableLogs: true
        },
        normalizeDepth: 11,
        maxValueLength: 1111,
        // A release identifier
        release: environment.SENTRY.VERSION.RELEASE,
        // A dist identifier
        dist: environment.SENTRY.VERSION.BUILD,
        // An environment identifier
        environment: environment.SENTRY.ENV,
        // We recommend adjusting this value in production, or using tracesSampler
        // for finer control
        tracesSampleRate: 0.2,
        // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        replaysOnErrorSampleRate: 0.1,
        // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysSessionSampleRate: 0.1,
        /**
         * Filter out error before send.
         *
         * @see https://docs.sentry.io/platforms/javascript/configuration/filtering
         *
         * @param {unknown} event Captured exception.
         * @param {unknown} hint Original exception.
         *
         * @returns {Event | null}
         */
        beforeSend: (event: any, hint: any): any => {
          // console.debug({ event, hint });

          const error: any = hint.originalException;
          if (error?.message?.match(/q._state|not enable|not implemented/i)) {
            return null;
          }
          return event;
        }
      });

      log.debug('ready');
    } catch (error: any) {
      log.debug('not ready');
      log.error(`throw an error, message="${error.message}"`, error);
    }
  }
}
